// Tablet + Home Internet pricing helpers.
// Promo schema (matches backend):
//   tablet_pricing: { standalone: {enabled, price, eligible_plan_ids}, bundle: {enabled, price, eligible_plan_ids, required_months} }
//   home_internet_pricing: { standalone: {enabled, price}, bundle: {enabled, price} }
//
// Variant selection rules (per user spec):
//   - Tablet line:  if hasPhones AND lineIdx < 2 → bundle only.   Else → standalone only.
//   - Home Internet line:  if hasPhones AND lineIdx < 1 → bundle only.  Else → standalone only.
// In other words, the customer never picks: the wizard auto-applies the correct variant
// based on context (line index + phone presence).

export const TABLET_BUNDLE_CAP = 2;        // first 2 tablet lines may use bundle pricing
export const HOME_INTERNET_BUNDLE_CAP = 1; // first 1 home internet line may use bundle pricing

/** Pull the tablet_pricing object from the active promo collection for a given device. */
export function tabletPricingFor(device, promos) {
  if (!device) return null;
  const p = (promos || []).find((pr) => pr.device_id === device.id && pr.tablet_pricing);
  return p?.tablet_pricing || null;
}

/** Pull the home_internet_pricing object from the active promo collection for a given device. */
export function homeInternetPricingFor(device, promos) {
  if (!device) return null;
  const p = (promos || []).find((pr) => pr.device_id === device.id && pr.home_internet_pricing);
  return p?.home_internet_pricing || null;
}

function bundleTablet(pricing) {
  return {
    key: "bundle",
    label: "Bundle (with phone)",
    price: pricing.bundle.price || 0,
    eligible_plan_ids: pricing.bundle.eligible_plan_ids || [],
    required_months: pricing.bundle.required_months || 0,
  };
}
function standaloneTablet(pricing) {
  return {
    key: "standalone",
    label: "Standalone",
    price: pricing.standalone.price || 0,
    eligible_plan_ids: pricing.standalone.eligible_plan_ids || [],
  };
}
function bundleHI(pricing) {
  return { key: "bundle", label: "Bundle (with phone)", price: pricing.bundle.price || 0, eligible_plan_ids: [] };
}
function standaloneHI(pricing) {
  return { key: "standalone", label: "Standalone", price: pricing.standalone.price || 0, eligible_plan_ids: [] };
}

/**
 * Returns the SINGLE applicable tablet variant for this line, or [] if none configured for the
 * required context. Customer no longer picks — the wizard auto-applies.
 */
export function tabletVariants(pricing, hasPhones, withinBundleCap) {
  if (!pricing) return [];
  if (hasPhones && withinBundleCap && pricing.bundle?.enabled) {
    return [bundleTablet(pricing)];
  }
  if (pricing.standalone?.enabled) {
    return [standaloneTablet(pricing)];
  }
  // No applicable variant — e.g. line is beyond bundle cap and standalone isn't enabled.
  // Fall back to whatever IS enabled so the customer can still see a price (better UX than crash).
  if (pricing.bundle?.enabled) return [bundleTablet(pricing)];
  return [];
}

/** Returns the SINGLE applicable HI variant — same rules as tabletVariants but cap=1. */
export function homeInternetVariants(pricing, hasPhones, withinBundleCap) {
  if (!pricing) return [];
  if (hasPhones && withinBundleCap && pricing.bundle?.enabled) {
    return [bundleHI(pricing)];
  }
  if (pricing.standalone?.enabled) {
    return [standaloneHI(pricing)];
  }
  if (pricing.bundle?.enabled) return [bundleHI(pricing)];
  return [];
}

/** True iff this tablet device has at least one variant the customer could see. */
export function tabletDeviceUsable(device, promos) {
  const p = tabletPricingFor(device, promos);
  if (!p) return false;
  return !!(p.standalone?.enabled || p.bundle?.enabled);
}

/** True iff this HI device has at least one variant. */
export function homeInternetDeviceUsable(device, promos) {
  const p = homeInternetPricingFor(device, promos);
  if (!p) return false;
  return !!(p.standalone?.enabled || p.bundle?.enabled);
}
